import React from "react";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { MdReorder, MdDelete } from "react-icons/md";
import {
  Box,
  IconButton,
  TextField,
  Typography,
  Paper,
} from "@mui/material";

const SortableItem = ({ value, onDelete, index, el_index, handleChange }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({
    id: value.cancellation_id
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Paper
      ref={setNodeRef}
      style={style}
      sx={{
        p: 1,
        mb: 1,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        '&:hover': {
          backgroundColor: 'action.hover',
        },
      }}
    >
      <TextField
        fullWidth
        size="small"
        value={value.reason}
        onChange={(e) => handleChange(e, el_index)}
        placeholder="Cancellation Reason"
        required
      />
      <IconButton
        color="error"
        size="small"
        onClick={(e) => onDelete(e, value.cancellation_id, el_index)}
      >
        <MdDelete />
      </IconButton>
      <IconButton
        {...listeners}
        {...attributes}
        size="small"
        sx={{ cursor: 'grab', '&:active': { cursor: 'grabbing' } }}
        title="Drag to reorder"
      >
        <MdReorder />
      </IconButton>
    </Paper>
  );
};

const SortableData = ({ items, onSortEnd, onDelete, handleChange }) => {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = items.findIndex((item) => item.cancellation_id === active.id);
      const newIndex = items.findIndex((item) => item.cancellation_id === over.id);
      onSortEnd({ oldIndex, newIndex });
    }
  };

  if (!items) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography color="text.secondary">Loading...</Typography>
      </Box>
    );
  }

  if (!items.length) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography color="text.secondary">No cancellation reasons added yet</Typography>
      </Box>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={items.map((item) => item.cancellation_id)}
        strategy={verticalListSortingStrategy}
      >
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}>
          {items.map((value, index) => (
            <SortableItem
              key={value.cancellation_id}
              value={value}
              index={index}
              el_index={index}
              handleChange={handleChange}
              onDelete={onDelete}
            />
          ))}
        </Box>
      </SortableContext>
    </DndContext>
  );
};

export default SortableData;